// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16 tokens + D-04 dark-ok)
// vg-binding: INTERFACE-STANDARDS:error-shape  (pure FE restyle — no API surface changed)
// apps/admin/src/pages/users/MerchantCreditTab.tsx
//
// Phase 3.4b Wave 7 Task 16 — Admin per-merchant credit history tab.
//
// Per CONTEXT.md
//   • Credit lifecycle and admin grants. Tab surfaces every credit
//                row issued to the merchant (active / overdue / expired /
//                revoked) with limit, remaining, period, and audit columns.
//   • RBAC: parent route gates by `credit:read`; revoke action
//                ride-on `credit:write` (BE-enforced — UI only hides buttons
//                on non-active rows, not on permission absence; the parent
//                drawer is responsible for the perm check).
//
// Goals: (revoke flow with reason gate — surface for the action)
//         (RBAC perm credit:read for the list itself)
//         (history list per-merchant — every credit row, not just active).
//
// Surface contract:
//   - Status filter (multi-select) synced to URL query `?status=` so the
//     filtered view is shareable / refresh-stable.
//   - Columns: Status badge | Limit | Remaining | Period (days) | StartDate
//     | EndDate | CreatedAt | CreatedBy.
//   - Per-row "Edit" + "Revoke" actions visible only when row.status==='active'.
//   - Per-row "View Details" expands the row to show CREDIT_USAGE +
//     CREDIT_PAYMENT transactions filtered by `(merchantId, type IN (...))`.
//   - Empty state ("merchant has no credit yet") renders a "Cấp credit đầu
//     tiên" CTA that opens `<CreditEditorDialog mode="create">` from Task 15.
//
// Sibling Task 15 imports — these MUST resolve at typecheck (Task 15 lands
// concurrently). Expected paths:
//   - useMerchantCreditHistory(merchantId, filters) → infinite/list query
//   - CreditEditorDialog (mode='create' | 'edit')
//   - useGetCredit(creditId) — single-credit lookup for view-details panel

import { useMemo, useState } from 'react';
import { useQuery, type InfiniteData } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@printway/ui';
import { apiClient } from '@printway/shared/api';
import type { CreditPayload } from '@printway/shared';

import { useMerchantCreditHistory } from '../../api/queries/credit.queries.js';
import type {
  MerchantCreditHistoryResponse,
  MerchantCreditHistoryRow,
} from '../../api/credit.api.js';
import { CreditEditorDialog } from './CreditEditorDialog.js';
import { RevokeCreditDialog } from './RevokeCreditDialog.js';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

type CreditStatus = 'active' | 'overdue' | 'expired' | 'revoked';

// Status enum values; human-facing labels resolved via i18n
// (`merchantCredit.status.<value>`) at render time.
const STATUS_VALUES: readonly CreditStatus[] = [
  'active',
  'overdue',
  'expired',
  'revoked',
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCentsUsd(cents: number): string {
  return ((cents ?? 0) / 100).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
}

function formatDate(value: string | number | null | undefined): string {
  if (!value) return '—';
  const ts = typeof value === 'number' ? value : Date.parse(value);
  if (!Number.isFinite(ts)) return '—';
  return new Date(ts).toLocaleDateString();
}

function statusBadgeClasses(status: CreditStatus | string): string {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200';
    case 'overdue':
      return 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200';
    case 'expired':
      return 'bg-pw-surface text-pw-text';
    case 'revoked':
      return 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-100';
    default:
      return 'bg-pw-surface text-pw-text';
  }
}

// ---------------------------------------------------------------------------
// View-details inline panel
// ---------------------------------------------------------------------------

interface CreditTransactionRow {
  txGroupId: string;
  type: 'CREDIT_USAGE' | 'CREDIT_PAYMENT';
  createdAt: number;
  // Signed-less magnitude of the ledger entry (cents). Sourced from the
  // ledger_entries collection by the API — tx_groups themselves carry no
  // amount, so metadata.amountCents was always undefined → $0.00.
  amountCents: number;
  metadata: Record<string, unknown>;
}

interface CreditTransactionsPage {
  rows: CreditTransactionRow[];
  hasMore: boolean;
  nextCursor: string | null;
}

interface SuccessEnvelope<T> {
  success: true;
  data: T;
}

function unwrapEnv<T>(payload: unknown): T {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as SuccessEnvelope<T>).success === true &&
    'data' in payload
  ) {
    return (payload as SuccessEnvelope<T>).data;
  }
  return payload as T;
}

interface LooseGetClient {
  GET: (path: string) => Promise<{
    data?: unknown;
    error?: unknown;
    response: Response;
  }>;
}

/**
 * Per-credit transactions read — filters tx_groups by `merchantId AND
 * type IN [CREDIT_USAGE, CREDIT_PAYMENT]` plus the active credit row.
 *
 * Wired to the admin merchant-credit transactions endpoint:
 *   GET /api/v1/admin/merchants/:merchantId/credit/:creditId/transactions
 *
 * The endpoint surface is owned by the Wave 7 BE wire-up — this hook
 * forward-trusts the contract; if the route shape changes the query will
 * surface a runtime error visible in the panel.
 */
function useMerchantCreditTransactions(args: {
  merchantId: string;
  creditId: string;
}) {
  const { merchantId, creditId } = args;
  return useQuery<CreditTransactionsPage, Error>({
    queryKey: [
      'admin',
      'merchant',
      merchantId,
      'credit',
      creditId,
      'transactions',
    ] as const,
    queryFn: async () => {
      const c = apiClient() as unknown as LooseGetClient;
      const path = `/api/v1/admin/merchants/${encodeURIComponent(
        merchantId,
      )}/credit/${encodeURIComponent(creditId)}/transactions?types=CREDIT_USAGE,CREDIT_PAYMENT`;
      const { data, error, response } = await c.GET(path);
      if (error || !response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return unwrapEnv<CreditTransactionsPage>(data);
    },
    enabled: Boolean(merchantId && creditId),
    staleTime: 30_000,
  });
}

function CreditTransactionsPanel({
  merchantId,
  creditId,
}: {
  merchantId: string;
  creditId: string;
}): JSX.Element {
  const { t } = useTranslation('users');
  const tx = useMerchantCreditTransactions({ merchantId, creditId });

  if (tx.isLoading) {
    return (
      <div className="px-3 py-3 text-xs text-pw-text-muted">
        {t('merchantCredit.tx.loading')}
      </div>
    );
  }
  if (tx.error) {
    return (
      <div className="px-3 py-3 text-xs text-red-600 dark:text-red-400">
        {t('merchantCredit.tx.error', {
          message:
            tx.error instanceof Error
              ? tx.error.message
              : t('merchantCredit.tx.unknown'),
        })}
      </div>
    );
  }
  const rows = tx.data?.rows ?? [];
  if (rows.length === 0) {
    return (
      <div className="px-3 py-3 text-xs text-pw-text-muted">
        {t('merchantCredit.tx.empty')}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
    <table className="min-w-full text-xs">
      <thead className="text-pw-text-muted">
        <tr>
          <th className="px-2 py-1 text-left font-medium">
            {t('merchantCredit.tx.columns.type')}
          </th>
          <th className="px-2 py-1 text-left font-medium">
            {t('merchantCredit.tx.columns.amount')}
          </th>
          <th className="px-2 py-1 text-left font-medium">
            {t('merchantCredit.tx.columns.when')}
          </th>
          <th className="px-2 py-1 text-left font-medium">
            {t('merchantCredit.tx.columns.txGroup')}
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-pw-border">
        {rows.map((r) => {
          const amountCents =
            typeof r.amountCents === 'number'
              ? r.amountCents
              : typeof r.metadata?.amountCents === 'number'
                ? (r.metadata.amountCents as number)
                : 0;
          return (
            <tr key={r.txGroupId} className="hover:bg-pw-surface">
              <td className="px-2 py-1 font-mono">{r.type}</td>
              <td className="px-2 py-1">{formatCentsUsd(amountCents)}</td>
              <td className="px-2 py-1">
                {new Date(r.createdAt).toLocaleString()}
              </td>
              <td className="px-2 py-1 font-mono text-pw-text-muted">
                {r.txGroupId.slice(0, 8)}…
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface MerchantCreditTabProps {
  merchantId: string;
  /** Optional display name for empty-state copy. */
  merchantDisplayName?: string;
  /** Merchant email — shown in the grant/edit dialog readonly field. */
  merchantEmail?: string;
}

export function MerchantCreditTab({
  merchantId,
  merchantDisplayName,
  merchantEmail,
}: MerchantCreditTabProps): JSX.Element {
  const { t } = useTranslation('users');
  const [searchParams, setSearchParams] = useSearchParams();

  // ---- Status filter (URL-synced) ----
  const statusParam = searchParams.get('status') ?? '';
  const selectedStatuses = useMemo<CreditStatus[]>(() => {
    if (!statusParam) return [];
    return statusParam
      .split(',')
      .map((s) => s.trim())
      .filter((s): s is CreditStatus =>
        STATUS_VALUES.includes(s as CreditStatus),
      );
  }, [statusParam]);

  function toggleStatus(value: CreditStatus): void {
    const next = new Set(selectedStatuses);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    const sp = new URLSearchParams(searchParams);
    if (next.size === 0) sp.delete('status');
    else sp.set('status', Array.from(next).join(','));
    setSearchParams(sp, { replace: true });
  }

  // ---- Data ----
  // BE accepts a single `status` filter (see MerchantCreditHistoryFilters in
  // credit.api.ts). Forward exactly one when the user picks one; when the
  // user picks multiple we omit the BE filter and narrow client-side. When
  // none picked we show everything. This matches the AuditLogPage forward-
  // compat pattern (single-server-value + client narrowing).
  const beStatusFilter =
    selectedStatuses.length === 1 ? selectedStatuses[0] : undefined;
  const history = useMerchantCreditHistory(
    merchantId,
    beStatusFilter ? { status: beStatusFilter } : {},
  );
  const allFetchedRows: MerchantCreditHistoryRow[] = useMemo(() => {
    // The Task-15 sibling hook narrows the data type to a single page, but
    // the TanStack runtime shape is always `InfiniteData<page>`. We re-cast
    // here so we can flatMap across all loaded pages without depending on
    // the upstream type-fix landing first.
    const infiniteData = history.data as
      | InfiniteData<MerchantCreditHistoryResponse>
      | undefined;
    const pages = infiniteData?.pages ?? [];
    return pages.flatMap((p) => p.rows);
  }, [history.data]);
  const rows: MerchantCreditHistoryRow[] = useMemo(() => {
    if (selectedStatuses.length <= 1) return allFetchedRows;
    return allFetchedRows.filter((r) =>
      selectedStatuses.includes(r.credit.status as CreditStatus),
    );
  }, [allFetchedRows, selectedStatuses]);

  // ---- Local UI state ----
  const [editorMode, setEditorMode] = useState<'create' | 'edit' | null>(null);
  const [editorCreditId, setEditorCreditId] = useState<string | null>(null);
  // Full existing-credit payload for edit mode — required so CreditEditorDialog
  // can diff against real values. Without it the dirty-check always reports
  // "no changes to save" (bug: edit limit 100→200 rejected).
  const [editorCredit, setEditorCredit] = useState<CreditPayload | null>(null);
  const [revokeFor, setRevokeFor] = useState<MerchantCreditHistoryRow | null>(
    null,
  );
  const [expandedCreditId, setExpandedCreditId] = useState<string | null>(null);

  function openCreate(): void {
    setEditorCreditId(null);
    setEditorMode('create');
  }
  function openEdit(credit: CreditPayload): void {
    setEditorCreditId(credit.creditId);
    setEditorCredit(credit);
    setEditorMode('edit');
  }
  function closeEditor(): void {
    setEditorMode(null);
    setEditorCreditId(null);
    setEditorCredit(null);
  }
  function toggleExpanded(creditId: string): void {
    setExpandedCreditId((cur) => (cur === creditId ? null : creditId));
  }

  // ---- Render ----
  const isLoading = history.isLoading;
  const isEmpty = !isLoading && rows.length === 0 && selectedStatuses.length === 0;
  const isFilteredEmpty =
    !isLoading && rows.length === 0 && selectedStatuses.length > 0;

  return (
    <section className="flex flex-col gap-3" data-testid="merchant-credit-tab">
      {/* Header */}
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold">{t('merchantCredit.heading')}</h2>
          <p className="text-xs text-pw-text-muted">
            {t('merchantCredit.description')}
          </p>
        </div>
        <Button
          variant="default"
          size="sm"
          onClick={openCreate}
          data-testid="merchant-credit-grant-new"
        >
          {t('merchantCredit.grantNew')}
        </Button>
      </header>

      {/* Status multi-select filter */}
      <div
        className="flex flex-wrap items-center gap-2"
        data-testid="merchant-credit-status-filter"
      >
        <span className="text-xs font-medium text-pw-text-muted">
          {t('merchantCredit.statusLabel')}
        </span>
        {STATUS_VALUES.map((value) => {
          const checked = selectedStatuses.includes(value);
          return (
            <label
              key={value}
              className={
                'inline-flex cursor-pointer items-center gap-1 rounded-md border px-2 py-1 text-xs ' +
                (checked
                  ? 'border-pw-primary bg-pw-primary/10 text-pw-primary'
                  : 'border-pw-border bg-pw-bg text-pw-text hover:bg-pw-surface')
              }
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={checked}
                onChange={() => toggleStatus(value)}
                data-testid={`merchant-credit-filter-${value}`}
              />
              {t(`merchantCredit.status.${value}`)}
            </label>
          );
        })}
        {selectedStatuses.length > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const sp = new URLSearchParams(searchParams);
              sp.delete('status');
              setSearchParams(sp, { replace: true });
            }}
          >
            {t('merchantCredit.clear')}
          </Button>
        ) : null}
      </div>

      {/* Table */}
      <div className="rounded-lg border border-pw-border bg-pw-bg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-pw-border text-sm">
            <thead className="bg-pw-surface text-xs uppercase tracking-wide text-pw-text-muted">
              <tr>
                <th className="px-3 py-2 text-left font-medium">
                  {t('merchantCredit.columns.status')}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {t('merchantCredit.columns.limit')}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {t('merchantCredit.columns.remaining')}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {t('merchantCredit.columns.period')}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {t('merchantCredit.columns.start')}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {t('merchantCredit.columns.end')}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {t('merchantCredit.columns.created')}
                </th>
                <th className="px-3 py-2 text-left font-medium">
                  {t('merchantCredit.columns.createdBy')}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t('merchantCredit.columns.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-pw-border">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={9}
                    className="px-3 py-8 text-center text-pw-text-muted"
                  >
                    {t('merchantCredit.loading')}
                  </td>
                </tr>
              ) : isEmpty ? (
                <tr>
                  <td colSpan={9} className="px-3 py-12">
                    <div
                      className="mx-auto flex max-w-md flex-col items-center gap-2 text-center"
                      data-testid="merchant-credit-empty-state"
                    >
                      <span className="text-base font-medium text-pw-text">
                        {t('merchantCredit.empty.title')}
                      </span>
                      <span className="text-sm text-pw-text-muted">
                        {merchantDisplayName
                          ? t('merchantCredit.empty.bodyWithName', {
                              name: merchantDisplayName,
                            })
                          : t('merchantCredit.empty.body')}
                      </span>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={openCreate}
                        data-testid="merchant-credit-empty-cta"
                      >
                        {t('merchantCredit.empty.cta')}
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : isFilteredEmpty ? (
                <tr>
                  <td
                    colSpan={9}
                    className="px-3 py-8 text-center text-pw-text-muted"
                    data-testid="merchant-credit-filtered-empty"
                  >
                    {t('merchantCredit.filteredEmpty')}
                  </td>
                </tr>
              ) : (
                rows.flatMap((row) => {
                  const c = row.credit;
                  const expanded = expandedCreditId === c.creditId;
                  const isActive = c.status === 'active';
                  const main = (
                    <tr key={c.creditId} className="hover:bg-pw-surface">
                      <td className="px-3 py-2 align-top">
                        <span
                          className={
                            'inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium ' +
                            statusBadgeClasses(c.status)
                          }
                          data-testid={`merchant-credit-status-${c.creditId}`}
                        >
                          {c.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-top">
                        {formatCentsUsd(c.limitCents)}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {formatCentsUsd(row.remainingCents)}
                      </td>
                      <td className="px-3 py-2 align-top">{c.periodDays}</td>
                      <td className="px-3 py-2 align-top">
                        {formatDate(c.startDate)}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {formatDate(c.endDate)}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {formatDate(c.createdAt)}
                      </td>
                      <td className="px-3 py-2 align-top text-pw-text-muted">
                        {row.createdByEmail ??
                          row.createdByName ??
                          c.createdByUserId ??
                          '—'}
                      </td>
                      <td className="px-3 py-2 text-right align-top">
                        <div className="flex flex-wrap justify-end gap-1">
                          {isActive ? (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEdit(c)}
                                data-testid={`merchant-credit-edit-${c.creditId}`}
                              >
                                {t('merchantCredit.rowActions.edit')}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setRevokeFor(row)}
                                data-testid={`merchant-credit-revoke-${c.creditId}`}
                              >
                                {t('merchantCredit.rowActions.revoke')}
                              </Button>
                            </>
                          ) : null}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleExpanded(c.creditId)}
                            data-testid={`merchant-credit-view-${c.creditId}`}
                          >
                            {expanded
                              ? t('merchantCredit.rowActions.hide')
                              : t('merchantCredit.rowActions.viewDetails')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                  if (!expanded) return [main];
                  const detail = (
                    <tr
                      key={`${c.creditId}-detail`}
                      className="bg-pw-surface/50"
                    >
                      <td colSpan={9} className="px-3 py-2">
                        <CreditTransactionsPanel
                          merchantId={merchantId}
                          creditId={c.creditId}
                        />
                      </td>
                    </tr>
                  );
                  return [main, detail];
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Editor dialog (Task 15 sibling) */}
      {editorMode === 'create' ? (
        <CreditEditorDialog
          open
          mode="create"
          merchantId={merchantId}
          merchantEmail={merchantEmail}
          onClose={closeEditor}
        />
      ) : null}
      {editorMode === 'edit' && editorCreditId ? (
        <CreditEditorDialog
          open
          mode="edit"
          merchantId={merchantId}
          merchantEmail={merchantEmail}
          creditId={editorCreditId}
          defaultValues={editorCredit ?? undefined}
          onClose={closeEditor}
        />
      ) : null}

      {/* Revoke dialog */}
      {revokeFor ? (
        <RevokeCreditDialog
          open
          onClose={() => setRevokeFor(null)}
          creditId={revokeFor.credit.creditId}
          merchantId={merchantId}
          limitCents={revokeFor.credit.limitCents}
          remainingCents={revokeFor.remainingCents}
        />
      ) : null}
    </section>
  );
}

export default MerchantCreditTab;
